import type { OnChangeFn, PaginationState, SortingState } from '@tanstack/react-table'
import { Ban, ShieldAlert } from 'lucide-react'
import { useState } from 'react'

import type {
  LedgerJournalEntryRow,
  LedgerJournalStatus,
  LedgerPostingSource,
} from '@/api/ledger-types'
import { DataTable, dataTableColumns, type DataTableColumn } from '@/components/data-table'
import { MoneyDisplay } from '@/components/money-display'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { branchDescription } from '@/lib/branch'
import { formatShortDate } from '@/lib/datetime'

import { JOURNAL_STATUS_LABEL, POSTING_SOURCE_LABEL } from './constants'
import { JournalDetailPanel } from './journal-detail'
import { LEDGER_PERMISSIONS, useLedgerPermissions } from './permissions'
import { useJournalList, useLedgerBranchReach } from './queries'
import { useDebounced } from './use-debounced'

/**
 * The journal enquiry.
 *
 * A list on the left of the eye and one entry beneath it, rather than a list and a separate route.
 * That is a constraint honestly taken rather than a preference: `/ledger/journals` is registered in
 * the router and a parameterised child of it is not, so a typed link to `/ledger/journals/$id` would
 * not compile. Master-and-detail on one route needs no registration, keeps the filters the operator
 * set, and is the shape a reconciliation is actually worked in — find the entry, read its lines, go
 * back to the list.
 *
 * The gate is on this component rather than only on the navigation item that leads here: the route
 * is directly addressable, and hiding a menu entry gates nothing.
 *
 * Reading works at any width. The manual journal is what refuses — see `manual-journal.tsx`.
 */

/** The endpoint takes no ordering parameter. Ordering the loaded page would be a claim about all. */
const UNSORTED: SortingState = []
const ignoreSortingChange: OnChangeFn<SortingState> = () => undefined

const SOURCES: LedgerPostingSource[] = [
  'Teller',
  'Loans',
  'Savings',
  'GeneralLedger',
  'System',
  'FieldCollection',
]

const STATUSES: LedgerJournalStatus[] = ['Posted', 'Reversed']

const ANY = '__any__'

const DEFAULT_PAGE_SIZE = 50

const helper = dataTableColumns<LedgerJournalEntryRow>()

const columns: DataTableColumn<LedgerJournalEntryRow>[] = helper.columns([
  helper.accessor('entryNumber', {
    header: 'Entry',
    enableSorting: false,
    cell: (context) => (
      <span className="font-mono text-xs">{context.row.original.entryNumber}</span>
    ),
    meta: { noWrap: true, widthClass: 'w-40', card: 'lead' },
  }),
  helper.accessor('description', {
    header: 'Description',
    enableSorting: false,
    cell: (context) => (
      <span className="font-medium text-heading">{context.row.original.description}</span>
    ),
    meta: { card: 'title' },
  }),
  helper.accessor('businessDate', {
    header: 'Business date',
    enableSorting: false,
    cell: (context) => (
      <span className="numeric text-xs tabular-nums">
        {formatShortDate(context.row.original.businessDate)}
      </span>
    ),
    meta: { noWrap: true, widthClass: 'w-32' },
  }),
  helper.accessor('valueDate', {
    header: 'Value date',
    enableSorting: false,
    // Marked when it differs, because a backdated posting is the one somebody is usually hunting.
    cell: (context) =>
      context.row.original.valueDate === context.row.original.businessDate ? (
        <span className="text-xs text-muted">Same</span>
      ) : (
        <span className="numeric text-xs tabular-nums text-warning-text">
          {formatShortDate(context.row.original.valueDate)}
        </span>
      ),
    meta: { noWrap: true, widthClass: 'w-32' },
  }),
  helper.accessor('source', {
    header: 'Source',
    enableSorting: false,
    cell: (context) => (
      <span className="text-xs">{POSTING_SOURCE_LABEL[context.row.original.source]}</span>
    ),
    meta: { noWrap: true, widthClass: 'w-32' },
  }),
  helper.accessor('postedByUsername', {
    header: 'Posted by',
    enableSorting: false,
    // The name, resolved server-side through the Access seam — the answer "who posted this" used
    // to require ops.audit.view for. "The system" where no operator was behind the posting.
    cell: (context) =>
      context.row.original.postedByUserId === null ? (
        <span className="text-xs text-muted">The system</span>
      ) : (
        <span className="text-xs">
          {context.row.original.postedByUsername ?? `user ${context.row.original.postedByUserId}`}
        </span>
      ),
    meta: { noWrap: true, widthClass: 'w-28' },
  }),
  helper.accessor('totalDebitMinorUnits', {
    header: 'Debits',
    enableSorting: false,
    // Integer pesewas through MoneyDisplay. Never divided by a hundred anywhere on the way here.
    cell: (context) => (
      <MoneyDisplay
        value={context.row.original.totalDebitMinorUnits}
        currency={context.row.original.currencyCode}
        symbol={false}
      />
    ),
    meta: { align: 'right', widthClass: 'w-36', cardLabel: 'Debits' },
  }),
  helper.accessor('totalCreditMinorUnits', {
    header: 'Credits',
    enableSorting: false,
    cell: (context) => (
      <MoneyDisplay
        value={context.row.original.totalCreditMinorUnits}
        currency={context.row.original.currencyCode}
        symbol={false}
      />
    ),
    meta: { align: 'right', widthClass: 'w-36', cardLabel: 'Credits' },
  }),
  helper.accessor('status', {
    header: 'Status',
    enableSorting: false,
    cell: (context) => (
      <div className="flex flex-wrap gap-1">
        <Badge tone={context.row.original.status === 'Reversed' ? 'danger' : 'success'}>
          {JOURNAL_STATUS_LABEL[context.row.original.status]}
        </Badge>
        {context.row.original.isReversal ? <Badge tone="warning">Reversal</Badge> : null}
      </div>
    ),
    meta: { widthClass: 'w-36', card: 'lead' },
  }),
])

export function JournalEnquirySection() {
  const permissions = useLedgerPermissions()
  const reach = useLedgerBranchReach()

  const [branchId, setBranchId] = useState<string | null>(null)
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [onValueDate, setOnValueDate] = useState(false)
  const [source, setSource] = useState<LedgerPostingSource | null>(null)
  const [status, setStatus] = useState<LedgerJournalStatus | null>(null)
  const [glAccountCode, setGlAccountCode] = useState('')
  const [reference, setReference] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [selected, setSelected] = useState<string | null>(null)

  /*
   * The two free-text filters settle before they reach the query key. Wiring them straight into it
   * would put one request per keystroke onto an endpoint that reads the journal table.
   */
  const settledAccountCode = useDebounced(glAccountCode.trim())
  const settledReference = useDebounced(reference.trim())

  const query = useJournalList(
    {
      branchId,
      fromDate,
      toDate,
      onValueDate,
      source,
      status,
      glAccountCode: settledAccountCode,
      reference: settledReference,
      page,
      pageSize,
    },
    permissions.canViewJournals,
  )

  if (!permissions.canViewJournals) {
    return (
      <div className="flex flex-col items-center gap-2 rounded-card border border-border-subtle bg-surface px-4 py-12 text-center">
        <ShieldAlert className="size-6 text-warning-text" aria-hidden />
        <p className="text-sm font-medium text-heading">Journals are restricted</p>
        <p className="max-w-lg text-xs text-muted">
          Reading the ledger&apos;s postings needs{' '}
          <span className="font-mono">{LEDGER_PERMISSIONS.journalView}</span>, which your role does
          not hold.
        </p>
      </div>
    )
  }

  const hasFilters =
    branchId !== null ||
    fromDate !== '' ||
    toDate !== '' ||
    source !== null ||
    status !== null ||
    glAccountCode.trim() !== '' ||
    reference.trim() !== ''

  const clearFilters = () => {
    setBranchId(null)
    setFromDate('')
    setToDate('')
    setOnValueDate(false)
    setSource(null)
    setStatus(null)
    setGlAccountCode('')
    setReference('')
    setPage(1)
  }

  const onPaginationChange: OnChangeFn<PaginationState> = (updater) => {
    const current: PaginationState = { pageIndex: page - 1, pageSize }
    const next = typeof updater === 'function' ? updater(current) : updater

    setPage(next.pageIndex + 1)
    setPageSize(next.pageSize)
  }

  return (
    <div className="space-y-4">
      <Alert tone="info">
        <Ban aria-hidden />
        <div className="space-y-1">
          <AlertTitle>Journals are immutable</AlertTitle>
          <AlertDescription>
            No screen here edits a posting, because no endpoint does and the ledger would refuse one.
            A correction is a reversal — a second entry that undoes the first, with both left in the
            book. Open an entry to reverse it.
          </AlertDescription>
        </div>
      </Alert>

      <div className="grid gap-3 rounded-card border border-border-subtle bg-surface p-3 lg:grid-cols-4">
        <div className="space-y-1.5">
          <Label htmlFor="journal-branch">Branch</Label>
          <Select
            value={branchId ?? ANY}
            onValueChange={(next) => {
              setBranchId(next === ANY ? null : next)
              setPage(1)
            }}
          >
            <SelectTrigger id="journal-branch">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>Every branch you can reach</SelectItem>
              {reach.branches.map((branch) => (
                <SelectItem key={branch.id} value={branch.id}>
                  {branchDescription(branch)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="journal-from">From</Label>
          <Input
            id="journal-from"
            type="date"
            value={fromDate}
            onChange={(event) => {
              setFromDate(event.target.value)
              setPage(1)
            }}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="journal-to">To</Label>
          <Input
            id="journal-to"
            type="date"
            value={toDate}
            onChange={(event) => {
              setToDate(event.target.value)
              setPage(1)
            }}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="journal-source">Source</Label>
          <Select
            value={source ?? ANY}
            onValueChange={(next) => {
              setSource(next === ANY ? null : (next as LedgerPostingSource))
              setPage(1)
            }}
          >
            <SelectTrigger id="journal-source">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>Any source</SelectItem>
              {SOURCES.map((candidate) => (
                <SelectItem key={candidate} value={candidate}>
                  {POSTING_SOURCE_LABEL[candidate]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="journal-status">Status</Label>
          <Select
            value={status ?? ANY}
            onValueChange={(next) => {
              setStatus(next === ANY ? null : (next as LedgerJournalStatus))
              setPage(1)
            }}
          >
            <SelectTrigger id="journal-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>Any status</SelectItem>
              {STATUSES.map((candidate) => (
                <SelectItem key={candidate} value={candidate}>
                  {JOURNAL_STATUS_LABEL[candidate]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="journal-account">GL account</Label>
          <Input
            id="journal-account"
            value={glAccountCode}
            autoComplete="off"
            spellCheck={false}
            className="font-mono"
            placeholder="Account code"
            onChange={(event) => {
              setGlAccountCode(event.target.value)
              setPage(1)
            }}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="journal-reference">Reference</Label>
          <Input
            id="journal-reference"
            value={reference}
            autoComplete="off"
            spellCheck={false}
            className="font-mono"
            placeholder="Entry number or reference"
            onChange={(event) => {
              setReference(event.target.value)
              setPage(1)
            }}
          />
        </div>

        <div className="flex items-end">
          <label className="flex items-center gap-2 text-sm text-body max-lg:min-h-11">
            <Checkbox
              checked={onValueDate}
              onCheckedChange={(next) => {
                setOnValueDate(next === true)
                setPage(1)
              }}
            />
            Match the dates on value date
          </label>
        </div>

        <div className="lg:col-span-4">
          <p className="text-xs text-muted">
            The date range is measured against the business date the entry posted on. Tick the box to
            measure it against the value date instead — the two differ on a backdated posting, which
            is usually the one being looked for.
          </p>
          {hasFilters ? (
            <Button variant="ghost" size="sm" className="mt-1" onClick={clearFilters}>
              Clear the filters
            </Button>
          ) : null}
        </div>
      </div>

      <DataTable
        columns={columns}
        data={query.data?.items}
        rowCount={query.data?.totalCount}
        pagination={{ pageIndex: page - 1, pageSize }}
        onPaginationChange={onPaginationChange}
        sorting={UNSORTED}
        onSortingChange={ignoreSortingChange}
        isLoading={query.isPending}
        isFetching={query.isFetching}
        error={query.isError ? query.error : null}
        onRetry={() => void query.refetch()}
        getRowId={(row) => row.publicId}
        onRowActivate={(row) => setSelected(row.publicId)}
        pageSizeOptions={[25, DEFAULT_PAGE_SIZE, 100, 200]}
        emptyTitle={hasFilters ? 'No entries match' : 'No entries yet'}
        emptyDescription={
          hasFilters
            ? 'Nothing in the ledger matches those filters. An entry number has to match exactly; widen the dates, or clear them.'
            : 'No branch you can reach has posted a journal. Entries arrive here from every module — a teller deposit, a disbursement, an accrual run — as well as from manual journals raised here.'
        }
        emptyAction={
          hasFilters ? (
            <Button variant="secondary" size="sm" onClick={clearFilters}>
              Clear the filters
            </Button>
          ) : null
        }
      />

      <JournalDetailPanel
        journalPublicId={selected}
        onReversalPosted={() => {
          // The reversal is a new entry, so the list has a row it did not have. The mutation
          // invalidates the key; this only makes sure the operator is looking at a fresh page.
          setPage(1)
        }}
      />
    </div>
  )
}
